import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import SEO from "./SEO";
import { useAppData } from "../context/AppDataContext";
import { filterToursForPublic } from "../constants/publicTours";

const TOUR_TITLES = {
  alicante: {
    hr: "Alicante – Escapada de Primavera",
    en: "Alicante – Spring Escape",
    de: "Alicante – Frühlingsausflug",
    es: "Alicante – Escapada de Primavera",
  },
  "alicante-maj": {
    hr: "Alicante – Escapada de Primavera (svibanj)",
    en: "Alicante – Spring Escape (May)",
    de: "Alicante – Frühlingsausflug (Mai)",
    es: "Alicante – Escapada de Primavera (mayo)",
  },
  "alicante-ozujak": {
    hr: "Alicante – Escapada de Primavera (ožujak)",
    en: "Alicante – Spring Escape (March)",
    de: "Alicante – Frühlingsausflug (März)",
    es: "Alicante – Escapada de Primavera (marzo)",
  },
  "madrid-segovia": {
    hr: "Madrid & Segovia – City Break",
    en: "Madrid & Segovia – City Break",
    de: "Madrid & Segovia – City Break",
    es: "Madrid & Segovia – City Break",
  },
  "od-madrida-do-vinograda": {
    hr: "Od Madrida do vinograda",
    en: "From Madrid to Vineyards",
    de: "Von Madrid zu den Weinbergen",
    es: "De Madrid a los viñedos",
  },
  "valladolid-1tjedan": {
    hr: "Valladolid učenje španjolskog jezika",
    en: "Valladolid – Spanish language course",
    de: "Valladolid – Spanischkurs",
    es: "Valladolid – curso de español",
  },
  "valladolid-2tjedan": {
    hr: "Valladolid učenje španjolskog jezika",
    en: "Valladolid – Spanish language course",
    de: "Valladolid – Spanischkurs",
    es: "Valladolid – curso de español",
  },
  "valladolid-1tjedan-lipanj": {
    hr: "Valladolid učenje španjolskog jezika (13.–21. 6. 2026.)",
    en: "Valladolid – Spanish course (13–21 June 2026)",
    de: "Valladolid – Spanischkurs (13.–21. Juni 2026)",
    es: "Valladolid – curso de español (13–21 jun 2026)",
  },
  "valladolid-2tjedan-lipanj": {
    hr: "Valladolid učenje španjolskog jezika (13.–27. 6. 2026.)",
    en: "Valladolid – Spanish course (13–27 June 2026)",
    de: "Valladolid – Spanischkurs (13.–27. Juni 2026)",
    es: "Valladolid – curso de español (13–27 jun 2026)",
  },
  valencia: {
    hr: "Valencia – Las Fallas (privatni aranžman)",
    en: "Valencia – Las Fallas (private arrangement)",
    de: "Valencia – Las Fallas (privates Arrangement)",
    es: "Valencia – Las Fallas (arreglo privado)",
  },
};

const TOUR_DESCRIPTIONS = {
  alicante: {
    hr: "Proljetni bijeg na Costa Blancu – vino, more i radionica paelle.",
    en: "Spring escape to the Costa Blanca – wine, sea and paella workshop.",
    de: "Frühlingsflucht an die Costa Blanca – Wein, Meer und Paella-Workshop.",
    es: "Escape primaveral a la Costa Blanca – vino, mar y taller de paella.",
  },
  "alicante-maj": {
    hr: "Proljetni bijeg 07.–10.05. – Ryanair, 590 €, min. 8 osoba.",
    en: "Spring escape 7–10 May – Ryanair, €590, min. 8 people.",
    de: "Frühlingsausflug 7.–10. Mai – Ryanair, 590 €, min. 8 Personen.",
    es: "Escapada 7–10 mayo – Ryanair, 590 €, mín. 8 personas.",
  },
  "alicante-ozujak": {
    hr: "Proljetni bijeg 07.–10.03. – Ryanair, 590 €, min. 8 osoba.",
    en: "Spring escape 7–10 March – Ryanair, €590, min. 8 people.",
    de: "Frühlingsausflug 7.–10. März – Ryanair, 590 €, min. 8 Personen.",
    es: "Escapada 7–10 marzo – Ryanair, 590 €, mín. 8 personas.",
  },
  "madrid-segovia": {
    hr: "Tapas, Rooftops & španjolska energija. Iberia, 760 €, 4 dana / 3 noćenja.",
    en: "Tapas, rooftops & Spanish energy. Iberia, €760, 4 days / 3 nights.",
    de: "Tapas, Rooftops & spanische Energie. Iberia, 760 €, 4 Tage / 3 Nächte.",
    es: "Tapas, rooftops y energía española. Iberia, 760 €, 4 días / 3 noches.",
  },
  "od-madrida-do-vinograda": {
    hr: "Fiesta San Lorenzo, Ribera del Duero i Segovia: 10.-15.09. ili 15.-20.10.2026.",
    en: "Fiesta San Lorenzo, Ribera del Duero and Segovia: 10-15 Sep or 15-20 Oct 2026.",
    de: "Fiesta San Lorenzo, Ribera del Duero und Segovia: 10.-15.09. oder 15.-20.10.2026.",
    es: "Fiesta San Lorenzo, Ribera del Duero y Segovia: 10-15 sep o 15-20 oct 2026.",
  },
  "valladolid-1tjedan": {
    hr: "Tečaj u Valladolidu, Madrid, Segovia i Salamanca – 1.790 €, kolovoz 2026.",
    en: "Course in Valladolid, Madrid, Segovia & Salamanca – €1,790, August 2026.",
    de: "Kurs in Valladolid, Madrid, Segovia & Salamanca – 1.790 €, August 2026.",
    es: "Curso en Valladolid, Madrid, Segovia y Salamanca – 1.790 €, agosto 2026.",
  },
  "valladolid-2tjedan": {
    hr: "Dva tjedna tečaja u Valladolidu, Madrid, Segovia i Salamanca – 2.490 €, kolovoz–rujan 2026.",
    en: "Two weeks in Valladolid, Madrid, Segovia & Salamanca – €2,490, Aug–Sep 2026.",
    de: "Zwei Wochen Kurs in Valladolid, Madrid, Segovia & Salamanca – 2.490 €, Aug./Sep. 2026.",
    es: "Dos semanas en Valladolid, Madrid, Segovia y Salamanca – 2.490 €, ago–sep 2026.",
  },
  "valladolid-1tjedan-lipanj": {
    hr: "Valladolid učenje španjolskog jezika: 13.–21. 6. 2026., 9 dana / 8 noći, 1.790 €.",
    en: "Valladolid Spanish course: 13–21 June 2026, 9 days / 8 nights, €1,790.",
    de: "Spanischkurs Valladolid: 13.–21. Juni 2026, 9 Tage / 8 Nächte, 1.790 €.",
    es: "Curso de español en Valladolid: 13–21 jun 2026, 9 días / 8 noches, 1.790 €.",
  },
  "valladolid-2tjedan-lipanj": {
    hr: "Valladolid učenje španjolskog jezika: 13.–27. 6. 2026., 14 dana / 13 noći, 2.490 €.",
    en: "Valladolid Spanish course: 13–27 June 2026, 14 days / 13 nights, €2,490.",
    de: "Spanischkurs Valladolid: 13.–27. Juni 2026, 14 Tage / 13 Nächte, 2.490 €.",
    es: "Curso de español en Valladolid: 13–27 jun 2026, 14 días / 13 noches, 2.490 €.",
  },
  valencia: {
    hr: "Las Fallas, privatni luksuzni apartman, vodič. Popunjeno – privatno.",
    en: "Las Fallas, luxury private apartment, guide. Full – private.",
    de: "Las Fallas, Luxus-Privatapartment, Betreuung. Ausgebucht – privat.",
    es: "Las Fallas, apartamento privado de lujo, guía. Completo – privado.",
  },
};

function formatDate(dateStr, lng) {
  const date = new Date(dateStr);
  return date.toLocaleDateString(lng || "hr-HR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export default function Blog() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lng = i18n.language || "hr";
  const base = lng.slice(0, 2);
  const { tours: currentTours } = useAppData();
  const publicTours = filterToursForPublic(currentTours);
  const sortedTours = [...publicTours].sort(
    (a, b) => new Date(a.date) - new Date(b.date)
  );

  return (
    <div className="app">
      <SEO 
        title={`${t("blog.pageTitle")} - TraveLina`}
        description={t("blog.pageSubtitle")}
        keywords="putovanja, blog putovanja, iskustva putovanja, TraveLina putovanja"
      />
      <Navbar />
      <main>
            <section className="section" style={{ paddingTop: "6rem" }}>
              <div className="container">

            <div className="blog-grid">
              {sortedTours.map((tour) => {
                const titleMap = TOUR_TITLES[tour.key];
                const title =
                  titleMap?.[base] ||
                  titleMap?.en ||
                  (typeof tour.key === "string" ? tour.key.replace(/-/g, " ") : "") ||
                  "Putovanje";
                const descMap = TOUR_DESCRIPTIONS[tour.key];
                const blogSnippet = tour.blogText?.[base] || tour.blogText?.en || "";
                const desc =
                  descMap?.[base] ||
                  descMap?.en ||
                  (blogSnippet ? blogSnippet.slice(0, 220).trim() + (blogSnippet.length > 220 ? "…" : "") : "") ||
                  t("blog.fallbackDescription", "Pogledajte detalje i raspored putovanja.");
                return (
                  <article
                    className="blog-card"
                    key={tour.id}
                    onClick={() => navigate(`/tour/${tour.key}`)}
                    style={{ cursor: "pointer" }}
                  >
                    <div
                      className="blog-image"
                      style={{ backgroundImage: `url(${tour.image})` }}
                    >
                      <div className="blog-tag">{tour.tag}</div>
                    </div>
                    <div className="blog-body">
                      <h2>{title}</h2>
                      <div className="blog-meta">
                        <span>📍 {tour.country}</span>
                        <span className="tour-meta-date">
                          🗓 {formatDate(tour.date, lng)}
                        </span>
                        <span>
                          ⏳ {tour.days} {t("blog.days")} / {tour.nights}{" "}
                          {t("tours.nights")}
                        </span>
                      </div>
                      <p className="blog-description">{desc}</p>
                      
                      {/* Blog tekst - kratki preview */}
                      {tour.blogText && tour.blogText[base] && tour.blogText[base].length > 0 && (
                        <div
                          className="blog-preview"
                          style={{
                            marginTop: "1rem",
                            padding: "1rem",
                            background: "#f9fafb",
                            borderRadius: "0.5rem",
                            border: "1px solid rgba(0, 0, 0, 0.06)",
                            fontSize: "0.9rem",
                            lineHeight: "1.6",
                            color: "#4b5563",
                            maxHeight: "100px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {tour.blogText[base].substring(0, 150)}...
                        </div>
                      )}
                      <div className="blog-footer">
                        <div className="blog-price">
                          <span className="blog-price-main">
                            {t("tours.from")} {tour.price} €
                          </span>
                          <span className="blog-price-note">
                            {t("tours.perPerson")}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn btn-cta-gold"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/tour/${tour.key}`);
                          }}
                        >
                          {t("blog.btnReadMore")}
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

